import { createEffect, createMemo, createSignal, onMount, Show, untrack } from "solid-js";
import {
  Outlet,
  useLocation,
  useNavigate,
  useRouterState,
  useSearch,
} from "@tanstack/solid-router";
import type { AbstractPowerSyncDatabase } from "@powersync/common";

import { appState, useInteraction, useInteractionFlag } from "@fleet/ui";
import { useTheme } from "@/providers/theme-provider";
import { useSearchPalette } from "@/features/search/hooks/use-search-palette";
import { usePageSwipeNav } from "../hooks/use-page-swipe-nav";

// Layout specific components and providers
import { AppBackground } from "@/components/app-background";
import { AppHeader } from "@/components/app-header";
import { OfflineBanner } from "@/components/offline-banner";
import { MAIN_CONTENT_ID, SkipToContent } from "@/components/skip-to-content";
import { CommandPalette } from "@/features/search/components/command-palette";

// Layout module parts
import { AppLayoutSidebar } from "./app-layout-sidebar";
import { AppLayoutDialogs } from "./app-layout-dialogs";
import { AppContentSkeleton } from "./app-content-skeleton";
import { useAppDialogs } from "../hooks/use-app-dialogs";
import { useAppShortcuts } from "../hooks/use-app-shortcuts";
import { getGlowColors, pageEntryKey } from "../helpers";

interface AppLayoutContentProps {
  // May be null while PowerSync's local DB is still initializing. The static
  // shell renders regardless; only the data-bound content region falls back to
  // a granular skeleton until the database is available.
  db: AbstractPowerSyncDatabase | null;
}

export function AppLayoutContent(props: AppLayoutContentProps) {
  onMount(() => {
    // Notify the InitialSyncOverlay that we are mounted and ready
    appState.markAsReady();
  });

  const navigate = useNavigate();
  const searchPalette = useSearchPalette();
  const location = useLocation();
  const routerState = useRouterState();
  const isNavigating = () => routerState().status === "pending";

  // Global search params for the `_app` shell (#15b): global overlays
  // (command palette + shortcut cheat-sheet) addressable by URL. `strict:false`
  // reads them from any child route without tying us to a specific path.
  const shellSearch = useSearch({ strict: false });
  const paletteParam = () => shellSearch().palette === true;
  const paletteQueryParam = () => {
    const { pq } = shellSearch();
    return typeof pq === "string" ? pq : "";
  };
  const shortcutsParam = () => shellSearch().shortcuts === true;

  const theme = useTheme();

  const { isSidebarOpen, setIsSidebarOpen, shortcutsOpen, setShortcutsOpen } =
    useAppDialogs(location);

  // ── URL ↔ global overlays bridge (#15b) ────────────────────────────────
  // Writes shell params while preserving the current page's other search params
  // (TanStack inherits the parent's). `replace` does not bloat history.
  const openPaletteUrl = (q?: string) => {
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        palette: true,
        pq: q && q !== "" ? q : undefined,
      }),
      replace: true,
    });
  };
  const closePaletteUrl = () => {
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({ ...prev, palette: undefined, pq: undefined }),
      replace: true,
    });
  };
  const setShortcutsUrl = (open: boolean) => {
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        shortcuts: open ? true : undefined,
      }),
      replace: true,
    });
  };

  // URL → state: reflects deep-link/reload/back-forward in the overlays.
  // NOTE (CC-16): state reads inside `untrack` — if the effect also
  // tracks isOpen()/shortcutsOpen(), opening the overlay state-first (⌘K, "?")
  // re-fires it with the params still OLD (navigate is async) and immediately CLOSES it.
  // It must react only to EXTERNAL URL changes.
  createEffect(() => {
    const want = paletteParam();
    untrack(() => {
      if (want && !searchPalette.isOpen()) {
        searchPalette.open();
      } else if (!want && searchPalette.isOpen()) {
        searchPalette.close();
      }
    });
  });
  createEffect(() => {
    const want = shortcutsParam();
    if (want !== untrack(shortcutsOpen)) {
      setShortcutsOpen(want);
    }
  });

  // state → URL: when shortcuts (?, palette ⌘K) open an overlay without going
  // through the URL, we reflect the change so the URL is always the source of truth.
  createEffect(() => {
    const open = searchPalette.isOpen();
    if (open && !paletteParam()) {
      openPaletteUrl();
    } else if (!open && paletteParam()) {
      closePaletteUrl();
    }
  });
  createEffect(() => {
    const open = shortcutsOpen();
    if (open !== shortcutsParam()) {
      setShortcutsUrl(open);
    }
  });

  // The term typed in the palette is reflected in `?pq=` (idempotent).
  const onPaletteQueryChange = (q: string) => {
    if (!searchPalette.isOpen()) {
      return;
    }
    if (q === paletteQueryParam()) {
      return;
    }
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({ ...prev, pq: q === "" ? undefined : q }),
      replace: true,
    });
  };

  useAppShortcuts({
    searchPalette,
    navigate,
    theme,
    setShortcutsOpen,
    setIsSidebarOpen,
  });

  const globalTheme = createMemo(
    () => theme.allThemes()?.find((r) => r.target_id === "global") ?? null,
  );
  const glows = createMemo(() => getGlowColors(globalTheme(), theme.tokens()));

  const handleCloseSearch = () => {
    searchPalette.close();
  };

  // The mobile nav drawer owns horizontal swiping (drag-to-dismiss) while it
  // is open. Nothing reads this flag yet in this repo (the page swipe below
  // is bound to its own surface element, not the window, so it cannot fire
  // underneath the drawer) — but it is the single declaration of who owns the
  // horizontal gesture, so a future window-bound gesture can suppress itself
  // against it without having to rediscover the drawer's state.
  useInteractionFlag({ "nav-drawer.open": isSidebarOpen });

  // --- Page-level swipe between the main sections -------------------------
  // A horizontal fling pages between Today / Calendar / Stats. It defers to the
  // per-page horizontal carousels (marked `data-swipe-own`) and suppresses
  // itself while a dialog or the command palette is open.
  const interaction = useInteraction();
  const [pageRef, setPageRef] = createSignal<HTMLDivElement>();
  const pageSwipe = usePageSwipeNav({
    pathname: () => location().pathname,
    navigate: (to) => void navigate({ to }),
    isBlocked: () => Boolean(interaction.state["dialog.open"] || interaction.state["palette.open"]),
    getSurfaceRef: () => pageRef(),
  });

  return (
    <>
      <AppBackground
        class="flex h-dvh text-foreground overflow-hidden"
        isBlur={theme.isBlur()}
        background={theme.tokens()?.background}
        patternType={theme.tokens()?.patternType}
        patternOpacity={theme.tokens()?.patternOpacity}
        {...glows()}
      >
        <SkipToContent />
        <AppLayoutSidebar isOpen={isSidebarOpen} setOpen={setIsSidebarOpen} />

        {/* ── Main content ───────────────────────────────────────── */}
        <main
          id={MAIN_CONTENT_ID}
          class="flex flex-1 flex-col overflow-hidden bg-transparent pt-[var(--safe-area-inset-top)] pr-[var(--safe-area-inset-right)] pb-[var(--safe-area-inset-bottom)] outline-none"
        >
          <AppHeader onMenuClick={() => setIsSidebarOpen(true)} isNavigating={isNavigating} />
          <OfflineBanner />
          <div class="flex flex-1 flex-col min-h-0 overflow-hidden pr-[var(--safe-area-inset-right)] pl-[var(--safe-area-inset-left)]">
            {/* Swipe surface: owns the page-level horizontal gesture.
                `touch-action: pan-y` keeps native vertical scroll while the
                recognizer claims the horizontal axis on engaged moves. */}
            <div
              class="flex flex-1 flex-col min-h-0 h-full overflow-y-auto overflow-x-clip"
              style={{ "touch-action": "pan-y" }}
              {...pageSwipe.handlers}
            >
              <div
                ref={setPageRef}
                class="flex flex-1 flex-col min-h-0 will-change-transform"
                style={{ transform: pageSwipe.transform(), transition: pageSwipe.transition() }}
              >
                <Show when={props.db} fallback={<AppContentSkeleton />}>
                  {/*
                    Page-entry animation (GPU: transform + opacity only). The
                    keyed <Show> re-mounts this wrapper whenever the *section*
                    changes (see `pageEntryKey`), which re-runs the
                    `animate-slide-in-up` keyframe on the fresh node. Param-only
                    changes within a section (day/calendar carousels paging
                    `/day/$date`) keep the same key, so the page entry never
                    re-fires on a swipe — the carousels own that motion.
                    `prefers-reduced-motion` neutralises the keyframe globally.
                  */}
                  <Show when={pageEntryKey(location().pathname)} keyed>
                    <div class="flex flex-1 flex-col min-h-0 h-full animate-slide-in-up will-change-[transform,opacity]">
                      <Outlet />
                    </div>
                  </Show>
                </Show>
              </div>
            </div>
          </div>
        </main>

        <AppLayoutDialogs shortcutsOpen={shortcutsOpen} setShortcutsOpen={setShortcutsOpen} />
      </AppBackground>

      {/* Command Palette — rendered OUTSIDE AppBackground to avoid blur compositor flash */}
      <CommandPalette
        isOpen={searchPalette.isOpen()}
        onClose={handleCloseSearch}
        initialQuery={paletteQueryParam()}
        onQueryChange={onPaletteQueryChange}
      />
    </>
  );
}
